/**
 * API Client — Phase 4a Permission Handling
 *
 * Centralized API client with permission-aware error handling and 403 response handling.
 */

import axios from 'axios';
import { handleAPIError } from './authUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add authorization header
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Handle errors gracefully
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Process error through permission-aware handler
    const processedError = handleAPIError(error);

    // For 403 errors, you might want to:
    // - Show a toast notification
    // - Dispatch a Redux action
    // - Call a callback

    // For now, attach the processed error to the original error
    error.processedError = processedError;

    return Promise.reject(error);
  }
);

// Convenience methods

export const api = {
  /**
   * GET request
   */
  get: (url, config = {}) => apiClient.get(url, config),

  /**
   * POST request
   */
  post: (url, data, config = {}) => apiClient.post(url, data, config),

  /**
   * PATCH request
   */
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),

  /**
   * PUT request
   */
  put: (url, data, config = {}) => apiClient.put(url, data, config),

  /**
   * DELETE request
   */
  delete: (url, config = {}) => apiClient.delete(url, config),
};

export default apiClient;
